/**
 * Pure helpers for the agents UX (CLE-321 FE-14).
 *
 * Kept stateless and side-effect free so they can be exercised directly
 * by Vitest without rendering the host components. Anything that needs
 * to flow into the UI (chip colors, CRM badge kind, onboarding state
 * machine) goes through one of these functions. Agent prices come only
 * from the BE estimate (agent-prices.ts, use-agent-prices.hook.ts).
 */

import type { AgentType, CrmStatus, OrgProfileData } from '@/types';

// =============================================================================
// Token-chip color thresholds
// =============================================================================

export type TokenChipColor = 'neutral' | 'amber' | 'red';

/**
 * Map a prompt token count to one of three chip colors. The thresholds
 * match the AgentRunPanel constants (soft warn at 2000, hard cap at 8000).
 *
 *   - neutral  : tokens < softWarn
 *   - amber    : softWarn <= tokens < hardCap
 *   - red      : tokens >= hardCap (BE will refuse to dispatch)
 */
export function getTokenChipColor(
  tokens: number,
  softWarn: number = 2000,
  hardCap: number = 8000
): TokenChipColor {
  if (tokens >= hardCap) return 'red';
  if (tokens >= softWarn) return 'amber';
  return 'neutral';
}

// =============================================================================
// Run guard
// =============================================================================

/**
 * Should the AgentRunPanel's Run button be disabled?
 *
 * Two reasons block dispatch:
 *   1. The prompt is over the BE's hard cap (8000 tokens). The BE will
 *      400 with MAX_PROMPT_TOKENS_EXCEEDED, but we'd rather not let the
 *      user even try.
 *   2. agent_type='custom' with an empty prompt. The PRD requires a
 *      prompt for custom agents; the FE blocks the dispatch and the BE
 *      returns 400 if we somehow let it through.
 *
 * Both checks are pure functions of the inputs the panel already has
 * locally -- exported as a helper so the per-state-transition logic is
 * unit-testable without rendering the panel.
 */
export function isRunDisabled(
  agentType: AgentType,
  promptDraft: string,
  promptTokens: number | undefined,
  hardCap: number = 8000
): boolean {
  if ((promptTokens ?? 0) >= hardCap) return true;
  if (agentType === 'custom' && promptDraft.trim().length === 0) return true;
  return false;
}

// =============================================================================
// Onboarding state machine
// =============================================================================

export type OnboardingStage =
  | 'collect_urls'
  | 'researching'
  | 'review_profile'
  | 'submitting';

export type OnboardingEvent =
  | { type: 'start_research' }
  | { type: 'research_succeeded' }
  | { type: 'research_failed' }
  | { type: 'edit_back' }
  | { type: 'submit_started' }
  | { type: 'submit_succeeded' }
  | { type: 'submit_failed' };

/**
 * Pure reducer for the OnboardingSetup four-stage flow. Extracting it
 * here lets us test all happy + sad transitions without a React tree.
 * Unknown events return the current stage unchanged.
 */
export function nextOnboardingStage(
  current: OnboardingStage,
  event: OnboardingEvent
): OnboardingStage {
  switch (current) {
    case 'collect_urls':
      return event.type === 'start_research' ? 'researching' : current;
    case 'researching':
      if (event.type === 'research_succeeded') return 'review_profile';
      if (event.type === 'research_failed') return 'collect_urls';
      return current;
    case 'review_profile':
      if (event.type === 'edit_back') return 'collect_urls';
      if (event.type === 'submit_started') return 'submitting';
      return current;
    case 'submitting':
      // submit_succeeded leaves the page (handled by parent). The reducer
      // treats it as terminal — same stage. submit_failed rolls back so
      // the admin can re-approve.
      if (event.type === 'submit_failed') return 'review_profile';
      return current;
    default:
      return current;
  }
}

/**
 * Whether a saved profile holds a draft worth reviewing: a company name or an
 * ICP. A setup run that found nothing leaves only the onboarding answers and
 * the counted sales team on the profile; the review stage would show empty
 * fields and an Approve button for it.
 */
export function profileHasDraft(
  profile: OrgProfileData | null | undefined
): boolean {
  const filled = (value: unknown) =>
    typeof value === 'string' && value.trim() !== '';
  return filled(profile?.company_name) || filled(profile?.icp);
}

// =============================================================================
// CRM badge classification
// =============================================================================

export type CrmStatusKind = 'in_crm' | 'no_crm' | 'timeout' | 'not_found';

/**
 * Collapse the four-state CRM status union into a single kind for the
 * badge view layer to dispatch on. Mirrors the visual states rendered
 * by CrmStatusBadge in structured-output.component.tsx.
 */
export function crmStatusKind(status: CrmStatus): CrmStatusKind {
  if (status.is_existing === true && status.matched_record) return 'in_crm';
  if (status.error === 'no_crm_connected') return 'no_crm';
  if (status.error === 'lookup_timeout') return 'timeout';
  return 'not_found';
}

// =============================================================================
// List <-> string helpers (shared by OnboardingSetup + ProfileSettings)
// =============================================================================

/**
 * Render a list field for a multi-line textarea. One entry per line.
 *
 * Critical: we do NOT join on commas. Individual entries can legitimately
 * contain a comma (e.g. "Director, Marketing Ops"), and a comma-based
 * round-trip would silently split them in two.
 */
export function toLines(xs: string[] | undefined): string {
  return (xs ?? []).filter(Boolean).join('\n');
}

/**
 * Parse a multi-line textarea into a list of entries.
 *
 * Splits ONLY on newlines — never on commas. Individual entries can
 * legitimately contain a comma (e.g. "Director, Marketing Ops"), and
 * comma-splitting destroys those. Each entry is trimmed; empties are
 * dropped.
 */
export function fromLines(raw: string): string[] {
  return raw
    .split('\n')
    .map((s) => s.trim())
    .filter((v) => v.length > 0);
}

// =============================================================================
// Onboarding research in-flight tracker
// =============================================================================
//
// Tracks an in-flight research run across page reloads / sidebar nav.
// localStorage so it survives full unmount; bounded by a 10-minute window
// so a silent BE failure doesn't trap the user forever in a fake
// researching stage. The mark is cleared once the new profile_data has
// been observed and surfaced in the review_profile stage.
//
// Two consumers read the mark:
//   - OnboardingSetup, to resume the researching stage on re-mount.
//   - AgentsContainer, to show OnboardingSetup even for already-onboarded
//     orgs while a re-run is in progress.

export const IN_FLIGHT_KEY = 'agents:research_in_flight';
export const IN_FLIGHT_WINDOW_MS = 10 * 60 * 1000;

export interface InFlightMark {
  /** ISO 8601 timestamp at click-time. */
  started_at: string;
  website_url: string;
  /**
   * Snapshot of profile_data at click time. The poll loop transitions
   * out of researching when the live profile_data differs from this
   * snapshot. We can't rely on modified_at alone because the BE's
   * intermediate URL-only upsert bumps modified_at before the LLM has
   * produced anything, which would fool the FE into showing the stale
   * (pre-rerun) profile in the review stage.
   */
  baseline_profile_json: string;
  /** The org being researched; a mark from another org is ignored. */
  organization_id?: string;
}

/**
 * Deterministic stringify. profile_data is stored as JSONB in Postgres,
 * which does NOT preserve key order on retrieval -- the same row read
 * twice can come back with a different key sequence. Plain JSON.stringify
 * would then produce two different strings for identical data and the
 * poll loop would think the BE wrote a new result and flip the user
 * back to review_profile prematurely (showing the SAME data they had,
 * which looks like "it auto-loaded the existing one"). Sorting keys
 * before serializing eliminates that false positive.
 */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return '[' + value.map(stableStringify).join(',') + ']';
  }
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return (
    '{' +
    keys
      .map(
        (k) =>
          JSON.stringify(k) +
          ':' +
          stableStringify((value as Record<string, unknown>)[k])
      )
      .join(',') +
    '}'
  );
}

/**
 * Read the in-flight mark from localStorage. Returns null if absent,
 * malformed, or older than IN_FLIGHT_WINDOW_MS. Older marks are
 * proactively cleaned up so a forgotten LLM call that silently died
 * doesn't permanently block the onboarding card.
 */
/**
 * One mark per org, so research running in one org survives a run started in
 * another. Marks written before this lived under the shared ``IN_FLIGHT_KEY``;
 * they are still read (and cleared) as a fallback and expire with the window.
 */
function inFlightKey(organizationId?: string): string {
  return organizationId ? `${IN_FLIGHT_KEY}:${organizationId}` : IN_FLIGHT_KEY;
}

export function readInFlight(organizationId?: string): InFlightMark | null {
  try {
    const key = localStorage.getItem(inFlightKey(organizationId))
      ? inFlightKey(organizationId)
      : IN_FLIGHT_KEY;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as InFlightMark;
    // A run another org started (before a switch) isn't this org's.
    if (
      organizationId &&
      parsed.organization_id &&
      parsed.organization_id !== organizationId
    ) {
      return null;
    }
    const ts = new Date(parsed.started_at).getTime();
    if (Number.isNaN(ts)) return null;
    if (Date.now() - ts > IN_FLIGHT_WINDOW_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeInFlight(mark: InFlightMark): void {
  try {
    localStorage.setItem(
      inFlightKey(mark.organization_id),
      JSON.stringify(mark)
    );
  } catch {
    // ignore (storage full / private mode)
  }
}

export function clearInFlight(organizationId?: string): void {
  try {
    localStorage.removeItem(inFlightKey(organizationId));
    // The pre-per-org mark, if this org's run was the one it tracked.
    const legacy = localStorage.getItem(IN_FLIGHT_KEY);
    const legacyOrg = legacy
      ? (JSON.parse(legacy) as InFlightMark).organization_id
      : undefined;
    if (legacy && (!legacyOrg || legacyOrg === organizationId)) {
      localStorage.removeItem(IN_FLIGHT_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * Cheap presence check used by AgentsContainer to decide whether to
 * render the OnboardingSetup card even for already-onboarded orgs.
 */
export function hasInFlightResearch(organizationId?: string): boolean {
  return readInFlight(organizationId) !== null;
}
